/**
 * Empty extension manifest.
 *
 * The platform imports `@afriannotate/extensions` wherever it offers an
 * extension point. The build resolves that name to the package named by
 * AFRI_EXTENSIONS_DIR when one is installed, and to this stub otherwise,
 * so the open platform always compiles with no extensions present.
 *
 * Shape (every list optional):
 *   navigation:   [{ label, to, icon?, when?: "signed_in" | "org_manager" | "staff",
 *                    after?: "projects" }]   // default: after Platform
 *   routes:       page descriptors like the ones in apps/labelstudio/src/pages/index.js
 *                 ({ title, path, exact?, component, layout?, context? })
 *   settingsTabs: {
 *     project:      { [PageName]: PageComponent }   // merged into the project settings menu
 *                   // a page may set menuSection ("labeling"; default: the last
 *                   // section) and appliesTo(project) -> boolean
 *     organization: [{ label, component }]           // rendered as extra org settings tabs
 *     platform:     [{ key, label, path, component, after? }] // extra Platform tabs + routes, after the tab named
 *   }
 *   dashboardCards: reserved for organisation dashboard cards
 *   importSources: extra ways of bringing data in, shown as tabs of the Import step:
 *                 [{ id, label, component, finish? }]. The component receives
 *                 { project, requiredKeys, labelConfig } and writes tasks itself.
 *                 In the New Project wizard it also receives { deferred: true,
 *                 state, setState }: it records what to bring in with setState,
 *                 and finish({ project, state, api }) -> { ok, message? } runs
 *                 on Save. The wizard takes initialImport={{ source, state }}.
 *   projectActions: buttons in a project's header on the Data Manager:
 *                 [{ id, component }]. The component receives { project, canManage }.
 *   exportCards:  extra cards on a project's Export page: [{ id, component }].
 *                 The component receives { project } and decides whether to show.
 *   configFields: readers for tags the extension adds, so the Import step knows
 *                 which task fields they use: [(element) => [{ field, kind?, list? }]]
 *                 with kind one of "images", "audio", "video", "PDFs", "text".
 *   configSchema: completion for the extension's tags in the labeling setup's code
 *                 editor: { tags: { TagName: { name, description, attrs, children? } },
 *                 inView: ["TagName"] }, in the shape of the platform's tags.json.
 *   apiEndpoints: { name: "METHOD:/path" } merged into the API client's endpoint map
 *   hostModes:    alternative ways of serving the same bundle on another host, e.g. a
 *                 public directory on its own subdomain. Each entry:
 *                 { key, isActive(appSettings), rootRedirect?, pageTitle?(pathname), baseTitle?,
 *                   chromelessPrefixes?: string[], allowAnonymous?: boolean,
 *                   noServiceWorker?: boolean  // host runs SW-free; a leftover worker is evicted
 *                   appFooter?: boolean       // false: the mode supplies its own footer
 *                   pathMapping?: { toRouterPath(p), toBrowserPath(p) },
 *                   brand?: { lockup, lockupDark?, alt, homeHref? },
 *                   authBrand?: { name, tagline? ("" = none), markUrl, markUrlDark?, receiptLink?: boolean },
 *                   docsHref?, docsLabel?, notificationsScope?, profileHref?,
 *   docsFab      — false hides the floating handbook link, for a mode
 *                  that already carries the page in its own navigation.
 *                   signInHref?, signedOutHref? }
 *
 * Route descriptors may also carry `chromeless: true`, which hides the platform
 * header and sidebar on that route (the page brings its own chrome).
 */
export const navigation = [];
export const routes = [];
export const settingsTabs = { project: {}, organization: [], platform: [], platformSettings: [] };
// Tabs of the Organization page, with their pages:
// ``{ key, label, path: "/reports", component, when?: "admin" | "manager",
// after?: <tab key>, replaces?: <tab key> }``. The page is mounted at
// ``/organization<path>`` and renders the organisation's own layout itself.
export const organizationTabs = [];
// Cards on a person's own Earnings tab, after their receipts:
// ``{ key, component }``. Shown to the person themself only.
export const earningsSections = [];
// Extra actions on the organisation's Payouts page: ``header`` components sit
// with the page's own buttons; ``run`` components sit in each run's row and
// receive ``{ run, onChange }`` (``onChange`` reloads the list); ``status``
// components add a line under a run's status and receive ``{ run }``.
export const payoutsPage = { header: [], run: [], status: [] };
export const dashboardCards = [];
// Panels under a project's own Insights (the analytics page): ``{ key, component }``,
// each given ``{ project }``.
// Panels on a project's Analytics page: [{ key, section, component }],
// section one of overview, quality, readiness, delivery, people; the
// component gets { project, rangeParams, Panel } and renders Panel cards.
export const analyticsPanels = [];
export const importSources = [];
export const projectActions = [];
export const exportCards = [];
export const configFields = [];
// Labeling configs built from a file: [{ id, label, tooltip?, accept, toConfig(text, fileName) -> { config, message? } }].
// Each gets a button beside Browse Templates; toConfig may throw with a message.
export const configImports = [];
// The Import step, in place of the platform's: { ImportPage } taking the same
// props (pages/CreateProject/Import/Import.jsx). Undefined: the platform's.
export const importStep = undefined;
// Panels beside the Data Manager's table: [{ id, event, component }]. Opened
// and closed by the Data Manager event `event`; the component receives
// { project, onLabelTask(taskId), onClose }.
export const dmPanels = [];
// Entries for the project settings search box: [{ label, tab, path, keywords }].
export const settingsIndex = [];
// Panels at the top of the New Project wizard's first step:
// [{ key, component, covers? }]. The component receives
// { project, apply(draft), reveal() }; ``draft`` may carry
// { title, description, plan, label_config, instructions } and the wizard
// fills its own fields with what is there. Nothing is created: the person
// still reviews every step and saves.
// ``covers: true`` keeps the step's own boxes hidden until there is
// something to look over: the panel stands in for them, and either
// ``apply`` or ``reveal`` brings them back.
export const wizardPanels = [];
export const configSchema = { tags: {}, inView: [] };
export const apiEndpoints = {};
export const hostModes = [];
// Columns an extension adds to a project's Members table:
// [{ key, label, shows(project) -> boolean, load(projectId) -> Promise<Record<userId, any>>,
//   render(member, loaded, { project, reload }) -> node }]. The page
// shows one only where ``shows`` says the project has such a thing;
// ``reload`` asks the page to load the column's data again.
export const memberColumns = [];
